import React, { useState, useEffect, useRef } from 'react';
import { HiUsers } from 'react-icons/hi2';
import { IoChevronDown } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaTrophy } from 'react-icons/fa';
import { IoTrashOutline } from 'react-icons/io5';
import { RiUserStarLine } from 'react-icons/ri';
import { IniciativaFormatada } from 'types/iniciativa';
import { ModalIniciativa } from '../../Iniciativas/components/ModalIniciativa';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito,
  registrarIniciativas
} from 'services/iniciativa-action.service';
import { getCategoriaCorHex, getClassificacaoLabel } from 'utils/iniciativa-categoria';
import iniciativaVideoImg from 'assets/img/2026/iniciativa_video.png';
import logoPodio from 'assets/img/2026/logo_podio.png';
import './IniciativaCard.scss';

interface VotarConfig {
  votada: boolean;
  onVotar?: () => void;
  disabled?: boolean;
  /** Mensagem exibida no estado desabilitado (ex.: limite atingido). */
  disabledLabel?: string;
  readonly?: boolean;
}

type IniciativaCardProps = IniciativaFormatada & {
  /** Quando informado, substitui o botão "VER MAIS DETALHES" por controles de votação. */
  votar?: VotarConfig;
  /** Sobrescreve o texto exibido no header da media-frame (default: "CLASSIFICADA ETAPA X"). */
  classificadaLabel?: React.ReactNode;
  /** Sobrescreve o ícone exibido no header da media-frame (default: troféu). */
  classificadaIcon?: React.ReactNode;
  /** Quando informada, faz o ModalIniciativa abrir com a moldura de pódio
   *  (gradiente ouro / prata / bronze) correspondente à posição. */
  podioPosition?: 1 | 2 | 3;
  /** Quando informado, substitui o texto de descrição pela lista do top 5 indicados. */
  topIndicados?: Array<{ posicao: number; nome: string }>;
  /** Versão enxuta: oculta descrição, autores e a barra de elogios/favoritos,
   *  mantendo apenas título, mídia e o botão de detalhes. */
  compact?: boolean;
  /** Repassado ao ModalIniciativa: oculta participantes não-válidos no modal
   *  de detalhes (usado apenas no pódio de premiação da home). */
  ocultarNaoValidos?: boolean;
};

export const IniciativaCard: React.FC<IniciativaCardProps> = (props) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(props.usuarioElogio || '');
  const complimentRef = useRef<HTMLDivElement>(null);

  // Estados para controle de ações
  const [isFavorited, setIsFavorited] = useState(props.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: props.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: props.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenDetails = async () => {
    setIsModalOpen(true);

    if (!user?._id) {
      return;
    }

    try {
      await registrarIniciativas(user._id, props.id);
    } catch (error) {
    }
  };

  const {
    titulo,
    descricao,
    autores,
    categoria,
    corCategoria,
    categoryPremio,
    imagem,
    videoUrl,
    originalVideoUrl,
    classificadaEtapa1,
    classificadaEtapa2
  } = props;

  const corCategoriaHex = getCategoriaCorHex(categoryPremio);

  // No modo de votação não exibimos a moldura "Classificada Etapa X" — a faixa
  // verde inferior já comunica o estado da iniciativa. Etapa 2 tem precedência
  // sobre Etapa 1 quando ambas estão marcadas.
  const isVotingMode = Boolean(props.votar);
  const showClassificadaEtapa2 = Boolean(classificadaEtapa2) && !isVotingMode;
  const showClassificadaEtapa1 =
    Boolean(classificadaEtapa1) && !classificadaEtapa2 && !isVotingMode;
  const showPodio = Boolean(props.podioPosition) && !isVotingMode;
  const showClassificada = showClassificadaEtapa1 || showClassificadaEtapa2 || showPodio;
  const classif = getClassificacaoLabel({
    classificadaEtapa1,
    classificadaEtapa2,
    position: props.podioPosition,
  });
  const cardClassName = [
    'iniciativa-card',
    showClassificada ? 'iniciativa-card--classificada' : '',
    showClassificadaEtapa2 ? 'iniciativa-card--classificada-etapa2' : '',
    props.podioPosition ? `iniciativa-card--podio-${props.podioPosition}` : '',
    props.votar?.votada ? 'iniciativa-card--voted' : '',
    props.compact ? 'iniciativa-card--compact' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tagElement = (
    <div
      className={`iniciativa-card__tag tag-${corCategoria}`}
      style={{ backgroundColor: corCategoriaHex }}
      title={categoria}
    >
      {categoria}
    </div>
  );

  const mediaElement = videoUrl ? (
    <a
      href={originalVideoUrl || videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="iniciativa-card__video-link"
      aria-label={`Assistir ao vídeo da iniciativa ${titulo} (abre em nova aba)`}
    >
      <img
        src={iniciativaVideoImg}
        alt=""
        aria-hidden="true"
        className="iniciativa-card__image"
      />
    </a>
  ) : (
    <img
      src={imagem}
      alt={`Imagem da iniciativa ${titulo}`}
      className="iniciativa-card__image"
      tabIndex={0}
    />
  );

  return (
    <>
      <div className={cardClassName} role="article" aria-label={`Iniciativa: ${titulo}`}>
        {showClassificada ? (
          <div className="iniciativa-card__media-frame">
            <div className="iniciativa-card__classificada-header">
              <span className="iniciativa-card__classificada-label">
                {props.classificadaIcon ?? (
                  showPodio
                    ? <img src={logoPodio} alt="" className="iniciativa-card__podio-logo" />
                    : <FaTrophy size={20} aria-hidden="true" />
                )}
                {props.classificadaLabel ?? (
                  <span className="iniciativa-card__classificada-text">
                    <span className="iniciativa-card__classificada-stage">
                      {classif.classificacao}
                    </span>
                  </span>
                )}
              </span>
              {tagElement}
            </div>
            <div className="iniciativa-card__image-container">{mediaElement}</div>
          </div>
        ) : (
          <div className="iniciativa-card__image-container">
            {mediaElement}
            {tagElement}
          </div>
        )}

        <div className="iniciativa-card__content">
          <h4 className="iniciativa-card__title" tabIndex={0}>{titulo}</h4>
          {!props.compact && (
          <>
          {props.topIndicados !== undefined ? (
            <div className="iniciativa-card__top-indicados-wrapper">
              <span className="iniciativa-card__top-indicados-label" tabIndex={0}>Indicados</span>
              {props.topIndicados.length === 0 ? (
                <p className="iniciativa-card__top-indicados-empty" tabIndex={0}>
                  Ainda sem indicações
                </p>
              ) : (
                <ul
                  className="iniciativa-card__top-indicados"
                  aria-label="Top 5 mais indicados"
                >
                  {props.topIndicados.map((p) => (
                    <li key={p.posicao} className="iniciativa-card__top-indicados-item">
                      <RiUserStarLine
                        className="iniciativa-card__top-indicados-icon"
                        size={16}
                        aria-hidden="true"
                      />
                      <span className="iniciativa-card__top-indicados-nome" tabIndex={0}>{p.nome}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="iniciativa-card__text" tabIndex={0}>{descricao}</p>
          )}

          <div className="iniciativa-card__author" tabIndex={0} aria-label={`Autores: ${autores}`}>
            <HiUsers size={16} aria-hidden="true" />
            <span>Autores: {autores}</span>
          </div>

          <div className="iniciativa-card__action-bar">
            <div className="iniciativa-card__action-left">
              <div className="iniciativa-card__icon-wrapper">
                <BsEmojiSmile size={20} aria-hidden="true" style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }} />
                {props.totalElogios > 0 && (
                  <span className="iniciativa-card__badge" aria-label={`${props.totalElogios} elogios`}>{props.totalElogios}</span>
                )}
              </div>
              <div className="iniciativa-card__compliment-container" ref={complimentRef}>
                <button
                  className="iniciativa-card__compliment-dropdown"
                  onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                  aria-expanded={isComplimentOpen}
                  aria-haspopup="listbox"
                  aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}` : 'Faça um elogio'}
                  disabled={loadingAction}
                  style={{
                    borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                    color: selectedCompliment ? '#5D5FEF' : '#666666'
                  }}
                >
                  <span>{selectedCompliment || 'Faça um elogio!'}</span>
                  <IoChevronDown size={16} aria-hidden="true" />
                </button>
                {isComplimentOpen && !loadingAction && (
                  <div className="iniciativa-card__compliment-menu" role="listbox" aria-label="Opções de elogio">
                    {complimentOptions.map((compliment) => (
                      <button
                        key={compliment}
                        className="iniciativa-card__compliment-option"
                        role="option"
                        aria-selected={selectedCompliment === compliment}
                        onClick={() => handleComplimentSelect(compliment)}
                      >
                        {compliment}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="iniciativa-card__action-right">
              <div className="iniciativa-card__icon-wrapper">
                <button
                  className="iniciativa-card__icon-btn"
                  aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                  onClick={handleToggleFavorite}
                  disabled={loadingAction}
                  aria-pressed={isFavorited}
                  style={{
                    opacity: loadingAction ? 0.6 : 1,
                    color: isFavorited ? '#5D5FEF' : '#666666'
                  }}
                >
                  {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                </button>
                {props.totalFavoritos > 0 && (
                  <span className="iniciativa-card__badge" aria-label={`${props.totalFavoritos} favoritos`}>{props.totalFavoritos}</span>
                )}
              </div>
            </div>
          </div>
          </>
          )}

          <button
            className="iniciativa-card__button"
            onClick={handleOpenDetails}
          >
            VER MAIS DETALHES
          </button>
        </div>

        {props.votar && (
          <div
            className={`iniciativa-card__vote-strip${props.votar.votada ? ' is-voted' : ''}${props.votar.disabled && !props.votar.votada ? ' is-disabled' : ''}${props.votar.readonly ? ' is-readonly' : ''}`}
          >
            {props.votar.votada ? (
              <>
                <span className="iniciativa-card__vote-strip-label" tabIndex={0}>
                  Você votou nesta iniciativa!
                </span>
                {!props.votar.readonly && (
                  <button
                    type="button"
                    className="iniciativa-card__vote-strip-action"
                    onClick={props.votar.onVotar}
                  >
                    REMOVER VOTO
                    <IoTrashOutline size={16} aria-hidden="true" />
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="iniciativa-card__vote-strip-action iniciativa-card__vote-strip-action--full"
                onClick={props.votar.onVotar}
                disabled={props.votar.disabled}
                aria-pressed={false}
              >
                {props.votar.disabled
                  ? (props.votar.disabledLabel || 'LIMITE ATINGIDO')
                  : 'VOTAR'}
              </button>
            )}
          </div>
        )}
      </div>

      <ModalIniciativa
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        iniciativa={props}
        podioPosition={props.podioPosition}
        ocultarNaoValidos={props.ocultarNaoValidos}
      />
    </>
  );
};
